import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Send, Loader2, FileText, Save, Printer, Mail, Lock, Globe, Sparkles, Tag, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Built-in portal doc categories (Phase F). Tenant may extend these in Portal Settings.
const BUILTIN_CATEGORIES = ["quote", "invoice", "contract", "notice", "other"];

/**
 * Phase B.5 / Phase F — Unified Document Dispatch Dialog.
 *
 * One dialog with checkboxes for every dispatch target. Reuses the renderer
 * once per submit. Phase F adds per-doc category + portal-share + append/replace
 * strategy overrides that flow through to ClientDocument.
 */
export default function DocumentDispatchDialog({ kind, docId, open, onOpenChange }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [format, setFormat] = useState("pdf");
  const [opts, setOpts] = useState({
    send_and_file: true,
    save_to_client_profile: false,
    save_to_device: false,
    send_to_client_portal: false,
    send_to_client_email: false,
    print: false,
    internal_only: false,
  });

  // Phase F portal sharing controls (per-doc overrides)
  const [category, setCategory] = useState(kind || "other");
  const [shareToPortal, setShareToPortal] = useState("inherit"); // 'inherit' | 'yes' | 'no'
  const [portalStrategy, setPortalStrategy] = useState("inherit"); // 'inherit' | 'append' | 'replace'
  const [customCategories, setCustomCategories] = useState([]);
  const [effective, setEffective] = useState(null); // tenant-level defaults preview

  // Pull tenant defaults + custom categories so the admin sees the inherited values.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/portal-settings`);
        if (!cancelled) {
          setEffective(res.data || null);
          setCustomCategories(res.data?.custom_categories || []);
        }
      } catch (_) { /* admin-only; ignore failures */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const toggle = (k) => {
    setOpts((o) => {
      const next = { ...o, [k]: !o[k] };
      if (k !== "send_and_file" && next.send_and_file) next.send_and_file = false;
      if (k === "internal_only" && next.internal_only) {
        next.send_to_client_email = false;
        next.send_to_client_portal = false;
      }
      return next;
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        ...opts,
        format,
        note_to_client: note,
        category,
        share_to_portal: shareToPortal === "inherit" ? null : (shareToPortal === "yes"),
        portal_strategy: portalStrategy === "inherit" ? null : portalStrategy,
      };
      const res = await axios.post(`${API}/documents/${kind}/${docId}/dispatch`, body);
      const r = res.data.results || {};
      if ((opts.save_to_device || opts.print) && res.data.file_b64) {
        const bytes = Uint8Array.from(atob(res.data.file_b64), (c) => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: res.data.mime });
        const url = URL.createObjectURL(blob);
        if (opts.print) window.open(url, "_blank");
        if (opts.save_to_device) {
          const link = document.createElement("a");
          link.href = url; link.download = res.data.filename; link.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
      const summary = Object.entries(r)
        .filter(([, v]) => v !== "skipped")
        .map(([k, v]) => `${k.replaceAll("_", " ")}: ${v}`)
        .join(" · ");
      toast.success(summary || "Done");
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally {
      setBusy(false);
    }
  };

  const renderRow = (id, label, Icon, hint) => (
    <label key={id} className="flex items-start gap-3 p-2 rounded-md border hover:border-emerald-400 cursor-pointer" htmlFor={`disp-${id}`}>
      <Checkbox checked={opts[id]} onCheckedChange={() => toggle(id)} id={`disp-${id}`} data-testid={`disp-opt-${id}`} />
      <div className="flex-1">
        <p className="text-sm font-medium flex items-center gap-1"><Icon className="h-3.5 w-3.5 text-emerald-600" /> {label}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </label>
  );

  const allCategories = [...new Set([...BUILTIN_CATEGORIES, ...customCategories])];
  const portalSharingActive = opts.send_and_file || opts.send_to_client_portal || opts.save_to_client_profile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="document-dispatch-dialog">
        <DialogHeader><DialogTitle>Send / File {kind}</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {renderRow("send_and_file", "Send & File (recommended)", Sparkles, "Saves to client profile + portal + emails the client (one shortcut)")}
          {renderRow("save_to_client_profile", "Save to client profile", Save, "Mark the doc as filed against this client")}
          {renderRow("send_to_client_portal", "Send to client portal", Globe, "Drop a notification into the client's portal feed")}
          {renderRow("send_to_client_email", "Email client", Mail, "Send via Resend with PDF attached")}
          {renderRow("save_to_device", "Save to my device", FileText, "Download the file locally now")}
          {renderRow("print", "Print", Printer, "Opens the PDF in a new tab for printing")}
          {renderRow("internal_only", "Internal only (do not send to client)", Lock, "Overrides external sends. Useful for drafts.")}

          {/* Phase F — per-doc portal sharing controls */}
          {portalSharingActive && (
            <div className="mt-3 p-3 border-2 border-dashed border-emerald-200 bg-emerald-50/40 rounded-md space-y-3">
              <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                <Layers className="h-3.5 w-3.5" /> Portal sharing
              </p>

              <div>
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1"><Tag className="h-3 w-3" /> Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-1 h-9" data-testid="disp-category-trigger">
                    <SelectValue placeholder="Choose category" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`disp-category-${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Share to client portal</label>
                <Select value={shareToPortal} onValueChange={setShareToPortal}>
                  <SelectTrigger className="mt-1 h-9" data-testid="disp-share-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit" data-testid="disp-share-inherit">
                      Inherit ({effective?.auto_send_to_portal ? "yes" : "no"})
                    </SelectItem>
                    <SelectItem value="yes" data-testid="disp-share-yes">Yes — show in portal</SelectItem>
                    <SelectItem value="no" data-testid="disp-share-no">No — keep private</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">When sharing</label>
                <Select value={portalStrategy} onValueChange={setPortalStrategy}>
                  <SelectTrigger className="mt-1 h-9" data-testid="disp-strategy-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit" data-testid="disp-strategy-inherit">
                      Inherit ({effective?.doc_strategy || "append"})
                    </SelectItem>
                    <SelectItem value="append" data-testid="disp-strategy-append">Append — keep prior copies</SelectItem>
                    <SelectItem value="replace" data-testid="disp-strategy-replace">Replace — supersede older versions</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="pt-2">
            <label className="text-xs font-medium text-muted-foreground">Format</label>
            <div className="flex gap-2 mt-1">
              {["pdf", "docx"].map((f) => (
                <button key={f} type="button" onClick={() => setFormat(f)} className={`px-3 py-1 text-xs rounded-full border ${format === f ? "bg-emerald-100 border-emerald-500 text-emerald-700" : "bg-muted/40"}`} data-testid={`disp-format-${f}`}>
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Note to client (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Quick note that gets included in the portal notification and email body…" data-testid="disp-note-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="disp-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Dispatch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
